/**
 * The org's control charts (plant-analytics S9, S10): typed wrappers over `/v1/twin/spc/*` plus their hooks.
 * Backend `internal/twin/spc_{handlers,store,series}.go`.
 *
 * What the server decides, and the web only draws:
 *   - which chart a tag gets (individual values, or hourly averages when the tag reads often enough), and why;
 *   - the control limits: estimated from the tag's own baseline, versioned, provisional until the baseline
 *     holds enough points, replaced only by a new version (the analysis, or a user's re-baseline);
 *   - every rule a point broke (a stored signal, live or historical; historical signals never alarm).
 * No sigma, limit or capability is computed in the browser.
 *
 * The episode and method fields of S9b (`series.episodes`, `summary.{signals,episodes,signals_truncated}`,
 * `limits.excluded.unstable_why`, the `*_between` and `*_sd` methods, point phase "pre") are optional here:
 * an older server leaves them out. The hooks live here, not in queries.ts or index.ts.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";

export type SpcChartType = "imr" | "xbar_r" | "xbar_s" | "p" | "np" | "u" | "laney_p" | "laney_u" | "ewma" | "cusum";

/** How a limits version was estimated. An older server sends the chart type. */
export type SpcMethod =
  | SpcChartType
  | "imr_sd"
  | "xbar_r_between"
  | "xbar_r_between_sd"
  | "xbar_s_between"
  | "xbar_s_between_sd";

export interface SpcParams {
  subgroup_size?: number;
  statistic?: "individual" | "subgroup_mean";
  lambda?: number;
  L?: number;
  k?: number;
  h?: number;
  /** Set on an EWMA companion: the chart it follows. */
  companion_of?: string;
  warnings?: string[];
}

/** What the series pipeline set aside, by reason (every exclusion is counted). */
export interface SpcExcludedCounts {
  quality_refused: number;
  entry_time_only: number;
  other_classes: number;
  inactive_state: number;
  idle_repeats: number;
  stuck_masked: number;
  censored: number;
  short_subgroups: number;
  not_sampled: number;
  no_outcome_days: number;
  no_defect_days: number;
  invalid_days: number;
  simulated_lots: number;
  removed_ooc: number;
  row_cap_hit: number;
}

export interface SpcFilters {
  source: "profile" | "window" | "none";
  stuck_value: number | null;
  ceiling: number | null;
  floor: number | null;
  idle_max_gap_s: number | null;
  dead: boolean;
  frozen: boolean;
  coarse_levels: number;
  active_state: string;
}

export interface SpcLimitsExcluded {
  counts: SpcExcludedCounts;
  filters?: SpcFilters;
  provisional_why?: string;
  /** S9b: phase I found more than 10% of the baseline out of control. */
  unstable_why?: string;
  /**
   * One series per port: set when the version was estimated over the series of a port fed by several sources, in the
   * series' unit. Absent: estimated over the chart's tag, in the tag's unit.
   */
  series?: { port: string; unit: string };
}

export interface SpcLimitsAux {
  [key: string]: number | undefined;
  method_version?: number;
  point_sigma?: number;
  means_ratio?: number;
  means_lag1?: number;
  lag1?: number;
  flagged_share?: number;
}

/** One limits version. */
export interface SpcLimits {
  id: string;
  chart_id: string;
  version: number;
  status: "active" | "superseded";
  provisional: boolean;
  method: SpcMethod;
  baseline_from: string;
  baseline_to: string;
  n_points: number;
  n_subgroups: number | null;
  subgroup_size: number | null;
  excluded: SpcLimitsExcluded;
  removed_ooc: number;
  center: number | null;
  sigma_within: number | null;
  sigma_overall: number | null;
  ucl: number | null;
  lcl: number | null;
  aux: SpcLimitsAux;
  stats_version: string;
  created_by: string;
  reason: string;
  created_at: string;
}

export interface SpcChart {
  id: string;
  subject_kind: "tag" | "rate";
  tag_id: string | null;
  rate_key: string | null;
  rate_subject: Record<string, unknown> | null;
  entity_id: string | null;
  chart_type: SpcChartType;
  params: SpcParams;
  filters: Record<string, unknown>;
  chosen_by: "engine" | "user";
  choice_reason: string;
  status: "active" | "paused" | "retired";
  evaluated_through: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  monitoring: { caught_up: boolean; behind: boolean; evaluated: number };
  /** The active limits; null when the chart has none. */
  limits: SpcLimits | null;
}

/** A profiled tag the analysis has not charted, and why. */
export interface SpcNotCharted {
  tag_id: string;
  name: string;
  entity_id: string;
  code: string;
  message: string;
  /**
   * Set for a port read from several sources (one series per port): it is listed once, on its anchor's tag, and these
   * are its sources' tag ids, so a widget bound to any of them finds the entry.
   */
  sources?: string[];
}

export interface SpcChartList {
  charts: SpcChart[];
  not_charted: SpcNotCharted[];
}

export interface SpcChartDetail {
  chart: SpcChart;
  /** Every limits version, newest first. */
  versions: SpcLimits[];
  /** Signal counts under the active limits, by origin. */
  signals: { live: number; historical: number };
}

/** One plotted point of a location chart. */
export interface SpcSeriesPoint {
  at: string;
  /** null: a gap (a stuck value, or no data), drawn as a break in the line. */
  value: number | null;
  center: number;
  ucl: number;
  lcl: number;
  /** Subgroup size, or castings. */
  n?: number;
  /** Rates: defectives or defects. */
  count?: number;
  /** At the sensor's ceiling or floor: plotted, never used for the limits. */
  censored?: "ceiling" | "floor";
  excluded?: "stuck" | "no_data";
  data_class?: string;
  /** "pre" (S9b): before the baseline; "I": inside it; "II": after it. */
  phase: "pre" | "I" | "II";
  beyond_limits: boolean;
}

/** One stored rule violation. */
export interface SpcSignal {
  id: string;
  chart_id: string;
  limits_id: string;
  rule: string;
  at: string;
  /** The sub-chart: i, mr, xbar, r, s, p, np, u, ewma, cusum. */
  subject_key: string;
  value: number | null;
  detail: Record<string, unknown>;
  origin: "live" | "historical";
  created_at: string;
}

/** S9b: consecutive signals of one rule on one sub-chart, folded into one event. */
export interface SpcEpisode {
  sub: string;
  rule: string;
  origin: "live" | "historical" | "mixed";
  first_at: string;
  last_at: string;
  points: number;
  max_abs_z: number | null;
  /** Where the run on one side of the center line began (a sustained episode). */
  run_from?: string | null;
  /** The process still sits on one side of its center line. */
  sustained: boolean;
}

export interface SpcStatPoint {
  at: string;
  /** The EWMA, or the upper CUSUM sum. */
  value: number;
  /** CUSUM: the lower sum. */
  c_minus?: number;
  ucl: number;
  lcl: number;
  beyond_limits: boolean;
}

export interface SpcSeriesSummary {
  points: number;
  beyond_limits: number;
  pct_beyond_limits: number | null;
  signals?: number;
  episodes?: number;
  signals_truncated?: boolean;
}

export interface SpcProvenance {
  method: string;
  limits_version: number;
  provisional: boolean;
  stats_version: string;
  chosen_by: string;
  choice_reason: string;
  data_classes: Record<string, number>;
  simulated: boolean;
  simulation_org: boolean;
  filters: SpcFilters | null;
  row_cap_hit: boolean;
  truncated: boolean;
  max_points: number;
  limits_from: string;
}

/** GET /spc/charts/{id}/series: what a control chart draws, from the server's limits only. */
export interface SpcSeries {
  chart: SpcChart;
  limits: SpcLimits;
  from: string;
  to: string;
  points: SpcSeriesPoint[];
  phase_one: { from: string; to: string; version: number; provisional: boolean; provisional_why: string };
  excluded: SpcExcludedCounts;
  summary: SpcSeriesSummary;
  /** The dispersion sub-chart (reading-to-reading change, range or spread within the hour). */
  dispersion?: {
    sub: string;
    center: number;
    ucl: number;
    lcl: number;
    points: { at: string; value: number | null }[];
  };
  statistic?: { kind: "ewma" | "cusum"; points: SpcStatPoint[]; started_at?: string };
  /** Signals under the active limits in the window. */
  signals: SpcSignal[];
  episodes?: SpcEpisode[];
  provenance: SpcProvenance;
  /**
   * One series per port: set for the chart of a port fed by several sources. The points are the port's series in
   * `unit`; at each break (the port changes source) the run tests start again. `limits_in_unit` is false while the
   * active limits are in another unit than the points; `unit_pending` then says why the analysis could not set them
   * again in `unit` (absent: the next analysis run sets them).
   */
  series?: {
    entity_id: string;
    port: string;
    unit: string;
    sources: string[];
    breaks: { from: string; at: string }[];
    limits_in_unit: boolean;
    unit_pending?: { code: string; message: string };
  };
}

export interface SpcSeriesWindow {
  from?: string;
  to?: string;
  maxPoints?: number;
}

export interface SpcBaselineRequest {
  from: string;
  to: string;
  reason: string;
}

export interface SpcChartListParams {
  /** active (the server's default), paused, retired or all. */
  status?: "active" | "paused" | "retired" | "all";
  tagId?: string;
  subjectKind?: "tag" | "rate";
}

const enc = encodeURIComponent;

export const spcApi = {
  listCharts: (params: SpcChartListParams = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.tagId) q.set("tag_id", params.tagId);
    if (params.subjectKind) q.set("subject_kind", params.subjectKind);
    const qs = q.toString();
    return api.get<SpcChartList>(`/v1/twin/spc/charts${qs ? `?${qs}` : ""}`);
  },
  getChart: (id: string) => api.get<SpcChartDetail>(`/v1/twin/spc/charts/${enc(id)}`),
  getSeries: (id: string, window: SpcSeriesWindow = {}) => {
    const q = new URLSearchParams();
    if (window.from) q.set("from", window.from);
    if (window.to) q.set("to", window.to);
    if (window.maxPoints) q.set("max_points", String(window.maxPoints));
    const qs = q.toString();
    return api.get<SpcSeries>(`/v1/twin/spc/charts/${enc(id)}/series${qs ? `?${qs}` : ""}`);
  },
  /** A user's re-baseline: limits version n+1 from [from, to). 422 baseline_too_small {need, have}. */
  postBaseline: (id: string, body: SpcBaselineRequest) =>
    api.post<{ limits: SpcLimits }>(`/v1/twin/spc/charts/${enc(id)}/limits`, { action: "baseline", ...body }),
};

/** Build-time flag: only when explicitly "true" do live fetches fire. */
const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** Live fetches require the build flag AND an authenticated, org-resolved session. */
function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

export const SPC_KEYS = {
  all: ["twin-spc"],
  lists: ["twin-spc", "charts"],
  list: (tagId: string) => ["twin-spc", "charts", tagId] as const,
  chart: (chartId: string) => ["twin-spc", "chart", chartId] as const,
  /** Every window of a chart's series (a prefix of `series`). */
  seriesOf: (chartId: string) => ["twin-spc", "series", chartId] as const,
  series: (chartId: string, w?: SpcSeriesWindow) =>
    ["twin-spc", "series", chartId, w?.from ?? "", w?.to ?? "", w?.maxPoints ?? 0] as const,
} as const;

/** A tag's active charts, and why the tag has none when it has none. */
export function useSpcChartsForTag(tagId: string | null | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: SPC_KEYS.list(tagId ?? ""),
    queryFn: () => spcApi.listCharts({ tagId: tagId as string }),
    enabled: live && !!tagId,
    staleTime: 60_000,
  });
}

/**
 * A chart's points, limits and signals over `window` (no window: the server's default, the last 30 days of a
 * tag). Refreshed each minute: new readings plot as they arrive, signals once the analysis has run.
 */
export function useSpcSeries(chartId: string | null | undefined, window?: SpcSeriesWindow) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: SPC_KEYS.series(chartId ?? "", window),
    queryFn: () => spcApi.getSeries(chartId as string, window),
    enabled: live && !!chartId,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/** Re-baseline a chart (org writers): new limits from a window, made active as version n+1. */
export function useSpcRebaseline(chartId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SpcBaselineRequest) => spcApi.postBaseline(chartId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SPC_KEYS.chart(chartId) });
      void qc.invalidateQueries({ queryKey: SPC_KEYS.seriesOf(chartId) });
      void qc.invalidateQueries({ queryKey: SPC_KEYS.lists });
    },
  });
}

// Plain words for the server's vocabulary.

const HOURLY = "Each point is the average of the first readings in a plant hour.";
const UNKNOWN_METHOD = "Limits set by the analysis from this process's own history.";
const DRIFT =
  "The series drifts, so the limits come from its overall spread and only a point beyond them counts as a signal.";

const METHODS: Record<string, { label: string; detail: string }> = {
  imr: {
    label: "Individual values",
    detail: "Each reading is a point. The limits come from how much one reading differs from the next.",
  },
  imr_sd: { label: "Individual values, overall spread", detail: `Each reading is a point. ${DRIFT}` },
  xbar_r: {
    label: "Hourly averages",
    detail: `${HOURLY} The limits come from the range of the readings within each hour.`,
  },
  xbar_s: {
    label: "Hourly averages",
    detail: `${HOURLY} The limits come from the spread of the readings within each hour.`,
  },
  xbar_r_between: {
    label: "Hourly averages, hour-to-hour spread",
    detail: `${HOURLY} The limits come from how much the averages move from one hour to the next.`,
  },
  xbar_s_between: {
    label: "Hourly averages, hour-to-hour spread",
    detail: `${HOURLY} The limits come from how much the averages move from one hour to the next.`,
  },
  xbar_r_between_sd: { label: "Hourly averages, overall spread", detail: `${HOURLY} ${DRIFT}` },
  xbar_s_between_sd: { label: "Hourly averages, overall spread", detail: `${HOURLY} ${DRIFT}` },
  p: {
    label: "Share defective",
    detail: "The share of castings with a defect each plant day. The limits widen on days with fewer castings.",
  },
  np: { label: "Defectives per day", detail: "The number of defective castings each plant day." },
  u: {
    label: "Defects per casting",
    detail: "Defects per casting each plant day. The limits widen on days with fewer castings.",
  },
  laney_p: {
    label: "Share defective, day-to-day spread allowed",
    detail:
      "The share of castings with a defect each plant day, with limits widened for the day-to-day variation this plant shows.",
  },
  laney_u: {
    label: "Defects per casting, day-to-day spread allowed",
    detail: "Defects per casting each plant day, with limits widened for the day-to-day variation this plant shows.",
  },
  ewma: {
    label: "Smoothed trend (EWMA)",
    detail:
      "A weighted running average of the readings. It shows a small, lasting shift sooner than the individual values do.",
  },
  cusum: {
    label: "Running drift (CUSUM)",
    detail:
      "Adds up small departures from the center line. It signals once they build past a set amount in one direction.",
  },
};

/** What a limits method means, in words: a short label for a chip and a sentence for its tooltip. */
export function spcMethodLabel(method: string): { label: string; detail: string } {
  return METHODS[method] ?? { label: "Control chart", detail: UNKNOWN_METHOD };
}

const RULES: Record<string, string> = {
  we1: "Past the control limits",
  we2: "2 of 3 past 2 standard deviations (rule 2)",
  we3: "4 of 5 past 1 standard deviation (rule 3)",
  we4: "8 in a row on one side (rule 4)",
  nelson3: "6 in a row steadily rising or falling",
  nelson4: "14 in a row alternating up and down",
  nelson7: "15 in a row within 1 standard deviation of the center",
  nelson8: "8 in a row more than 1 standard deviation out, on either side",
  ewma: "EWMA beyond its limits",
  cusum_upper: "CUSUM above its decision interval (upper)",
  cusum_lower: "CUSUM above its decision interval (lower)",
};

/** The rule a signal broke, in words. */
export function spcRuleLabel(rule: string): string {
  return RULES[rule] ?? `Signal (${rule})`;
}

const SUB_CHARTS: Record<string, string> = {
  i: "Individual values",
  mr: "Reading-to-reading change",
  xbar: "Hourly averages",
  r: "Range within each hour",
  s: "Spread within each hour",
  p: "Share defective",
  np: "Defectives per day",
  u: "Defects per casting",
  ewma: "Smoothed trend (EWMA)",
  cusum: "Running drift (CUSUM)",
};

/** A sub-chart (a signal's subject_key, an episode's sub) in words. */
export function spcSubChartLabel(sub: string): string {
  return SUB_CHARTS[sub] ?? sub;
}

/** A chart type's location sub-chart: the one its points plot. */
export function spcLocationSub(chartType: string): string {
  switch (chartType) {
    case "imr":
      return "i";
    case "xbar_r":
    case "xbar_s":
      return "xbar";
    case "p":
    case "laney_p":
      return "p";
    case "u":
    case "laney_u":
      return "u";
    default:
      return chartType; // np, ewma, cusum
  }
}

const SMOOTHED = new Set(["ewma", "cusum"]);

/**
 * The chart a tag's tile draws: an active chart that is not an EWMA companion (params.companion_of set),
 * a location chart (individual values, hourly averages, a rate) before a smoothed one (EWMA, CUSUM), one with
 * active limits before one without; otherwise the server's order (oldest first). null when there is none.
 */
export function spcPickLocationChart(charts: readonly SpcChart[]): SpcChart | null {
  const rank = (c: SpcChart) => (SMOOTHED.has(c.chart_type) ? 2 : 0) + (c.limits ? 0 : 1);
  let best: SpcChart | null = null;
  for (const c of charts) {
    if (c.status !== "active" || c.params?.companion_of) continue;
    if (!best || rank(c) < rank(best)) best = c;
  }
  return best;
}

/** Why the analysis could not set a chart's limits again in its port's unit (`series.unit_pending.code`), in words. */
const UNIT_PENDING: Record<string, string> = {
  too_few_observations: "its baseline window holds too few readings",
  no_spread: "the readings in its baseline window do not vary enough",
  not_measured: "its baseline window holds no usable readings",
  not_profiled: "the port's readings have not been profiled yet",
};

/**
 * Why a chart's control limits are not drawn over its points, in plain words; null when they are drawn. The chart of a
 * port fed by several sources plots the port's one series in the port's unit. While its limits are in another unit
 * (`series.limits_in_unit` false: a source in another unit joined the port, and the analysis has not set them again),
 * drawing them over the points would compare values in two units, so the chart hides them and says why. When the
 * analysis tried and could not (`series.unit_pending`), the chart says it waits for a new baseline.
 */
export function spcLimitsHiddenText(series: SpcSeries | undefined): string | null {
  const s = series?.series;
  if (!s || s.limits_in_unit !== false) return null;
  const unit = s.unit?.trim() ? s.unit.trim() : "the port's unit";
  const lead = `The control limits are hidden because they are in another unit than the points, which are in ${unit}.`;
  if (!s.unit_pending) return `${lead} The next analysis run sets them again in ${unit}.`;
  const why = UNIT_PENDING[s.unit_pending.code] ?? "its baseline window gives no chart";
  return (
    `${lead} The analysis could not set them again because ${why}. ` +
    `No new signal is raised on this chart until it has a new baseline in ${unit}.`
  );
}

const NOT_CHARTED: Record<string, string> = {
  state_tag: "it is an on/off state, not a measured value.",
  setpoint: "it is a setpoint (a command), not a measurement of the process.",
  not_measured: "it has no usable readings yet.",
  dead_channel: "every reading is zero, so the channel looks dead.",
  frozen_channel: "the reading never changes, so there is nothing to chart.",
  pending: "the next analysis run charts it once it holds enough readings.",
};

/**
 * Why a tag has no control chart, from the list's not_charted entry for it: its own, or that of the port it is one of
 * the sources of (a port read from several sources is charted once, as its port).
 */
export function spcNotChartedText(list: SpcChartList | undefined, tagId: string): string {
  const nc = list?.not_charted?.find((n) => n.tag_id === tagId || (n.sources ?? []).includes(tagId));
  if (!nc) return "The analysis has not charted this tag yet.";
  const why = NOT_CHARTED[nc.code] ?? nc.message.replace(/^no chart yet:\s*/i, "");
  return `No control chart yet: ${why}`;
}
